import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { db as firestoreDb } from "@/src/lib/firebase";
import { collection, query, where, getDocs, orderBy, doc, updateDoc } from "firebase/firestore";
import { Meeting } from "../types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Video, Clock, User, Loader2, ExternalLink, Cake, Phone, Globe, MapPin as MapPinIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

export function ProfilePage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState({
    displayName: "",
    birthDate: "",
    phoneNumber: ""
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || "",
        birthDate: profile.birthDate || "",
        phoneNumber: profile.phoneNumber || ""
      });
    }
  }, [profile]);

  useEffect(() => {
    const fetchMyMeetings = async () => {
      if (!user) return;
      try {
        const q = query(
          collection(firestoreDb, "meetings"),
          where("buyerUid", "==", user.uid),
          orderBy("startTime", "desc")
        );
        const snap = await getDocs(q);
        setMeetings(snap.docs.map(d => ({ ...d.data(), id: d.id } as Meeting)));
      } catch (error) {
        console.error("Error fetching my meetings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyMeetings();
  }, [user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsUpdating(true);
    try {
      await updateDoc(doc(firestoreDb, "users", user.uid), {
        ...formData,
        updatedAt: new Date().toISOString()
      });
      toast.success("Perfil actualizado");
      setIsEditModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error al actualizar perfil");
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) {
    return (
      <div className="container py-24 text-center">
        <h1 className="text-2xl font-bold mb-4">Debes iniciar sesión</h1>
        <Button onClick={() => navigate("/login")}>Ir al Login</Button>
      </div>
    );
  }

  return (
    <div className="container py-12 px-4 md:px-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mi Perfil</h1>
          <p className="text-muted-foreground">Gestiona tus citas y actividad en EmpreMarket.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-10 w-10 text-primary" />
                </div>
                <div>
                  <p className="font-bold">{profile?.displayName || user.displayName || "Usuario"}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <div className="space-y-3 pt-2">
                {profile?.phoneNumber && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {profile.phoneNumber}
                  </div>
                )}
                {profile?.birthDate && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Cake className="h-3.5 w-3.5 text-pink-500" /> Cumpleaños: {new Date(profile.birthDate).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    Editar Perfil
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Editar Mi Perfil</DialogTitle>
                    <DialogDescription>Actualiza tu información personal para recibir beneficios.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUpdateProfile} className="space-y-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="display-name">Nombre Público</Label>
                      <Input 
                        id="display-name" 
                        value={formData.displayName} 
                        onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="phone">Teléfono de contacto</Label>
                      <Input 
                        id="phone" 
                        value={formData.phoneNumber} 
                        onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="birth-date">Fecha de Nacimiento</Label>
                      <Input 
                        id="birth-date" 
                        type="date"
                        value={formData.birthDate} 
                        onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                      />
                      <p className="text-[10px] text-muted-foreground">Esencial para recibir tu cupón de 10% el día de tu cumpleaños.</p>
                    </div>
                    <DialogFooter>
                      <Button type="submit" className="w-full" disabled={isUpdating}>
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : "Guardar Cambios"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Mis Citas y Reuniones
                </CardTitle>
                <CardDescription>Aquí aparecerán las reuniones que has agendado con las tiendas.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : meetings.length === 0 ? (
                  <div className="text-center py-12 border rounded-xl border-dashed">
                    <p className="text-muted-foreground">No has agendado ninguna cita aún.</p>
                    <Button variant="link" onClick={() => navigate("/shops")}>Explorar tiendas</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {meetings.map(meeting => (
                      <div key={meeting.id} className="border rounded-xl p-4 hover:shadow-md transition-all bg-card">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold">{meeting.title}</h4>
                              <Badge variant={
                                meeting.status === 'scheduled' ? 'default' :
                                meeting.status === 'pending' ? 'secondary' :
                                meeting.status === 'completed' ? 'outline' : 'destructive'
                              }>
                                {meeting.status === 'scheduled' ? 'Confirmada' :
                                 meeting.status === 'pending' ? 'Pendiente' :
                                 meeting.status === 'completed' ? 'Finalizada' : 'Cancelada'}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(meeting.startTime).toLocaleDateString()}</span>
                              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {new Date(meeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>

                          {meeting.status === 'scheduled' && meeting.meetLink && (
                            <Button 
                              size="sm" 
                              className="gap-2"
                              onClick={() => {
                                if (meeting.meetLink?.startsWith('/meeting/')) {
                                  navigate(meeting.meetLink);
                                } else {
                                  window.open(meeting.meetLink, "_blank");
                                }
                              }}
                            >
                              <Video className="h-4 w-4" />
                              Unirse a la Reunión
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
